<template>
    <div>
        <v-container fluid>
            <!-- Data Table Card -->
            <v-card elevation="0" class="border-top-primary">
              <div class="d-flex">
                  <h2 class="font-weight-bolder d-flex align-center">
                    {{ $t("farm.farmActivityLog") }}
                    <v-icon color="primary" class="ml-2">mdi-alert-circle</v-icon>
                  </h2>
              </div>
                <v-card-text>
                    <v-row style="margin-left: -30px; margin-bottom: 20px;">
                        <v-col cols="12">
                            <v-data-table :headers="tableHeaders" :items="activities" :items-per-page="options.limit"
                                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }" item-key="id"
                                hide-default-footer :loading="loading" :options.sync="options"
                                :server-items-length="totalActivities" :loading-text="$t('farm.loadingData')">
                                <template v-slot:top>
                                    <div class="d-flex">
                                        <!-- User Search Field -->
                                        <div class="ml-3 control" style="width: 280px;">
                                            <label for="user-search">{{ $t("farm.user") }}</label>
                                            <v-text-field prepend-inner-icon="mdi-magnify" outlined :placeholder="$t('farm.search')"
                                                dense v-model="search" @input="searchQuery"
                                                class="shrink"></v-text-field>
                                        </div>

                                        <!-- User Role Dropdown -->
                                        <div class="ml-3 control">
                                            <label>{{ $t("farm.userRole") }}</label>
                                            <v-select v-model="userRole" :items="userRoleList" item-text="name"
                                                item-value="value" outlined dense :placeholder="$t('farm.select')"
                                                @change="handleRoleChange" style="width: 180px;" class="shrink"
                                                clearable></v-select>
                                        </div>
                                            
                                        <v-spacer></v-spacer>

                                        <div class="mt-4 d-flex">
                                        <!-- Pagination Info -->
                                        <div class="py-1 title">
                                            {{ currentPageStart }} {{ $t("farm.of") }} {{ totalActivities }}
                                        </div>

                                        <!-- Pagination Controls -->
                                        <v-btn class="mx-2" fab small outlined color="primary"
                                            :disabled="options.page <= 1" @click="pageChange(false)">
                                            <v-icon dark>mdi-chevron-left</v-icon>
                                        </v-btn>
                                        <v-btn class="mx-2" fab small outlined color="primary"
                                            :disabled="options.page >= totalPages" @click="pageChange(true)">
                                            <v-icon dark>mdi-chevron-right</v-icon>
                                        </v-btn>

                                        <!-- Customization Menu -->
                                        <v-menu v-model="menu" :close-on-content-click="false" :nudge-width="200" left
                                            :min-width="417">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs"
                                                    v-on="on">
                                                    <v-icon dark>mdi-cog-outline</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-card width="417">
                                                <v-list>
                                                    <v-list-item>
                                                        <v-list-item-title>{{ $t("farm.customization") }}</v-list-item-title>
                                                    </v-list-item>
                                                    <v-divider></v-divider>
                                                    <div class="pl-3 pt-2 font-weight-normal">
                                                        {{ $t("farm.recordsPerPage") }}
                                                    </div>
                                                    <v-list-item>
                                                        <v-slider v-model="itemsPerPageSlider"
                                                            :tick-labels="itemsPerPageOptions" :max="3" step="1"
                                                            ticks="always" tick-size="4"></v-slider>
                                                    </v-list-item>
                                                    <v-divider class="mt-n3"></v-divider>
                                                </v-list>
                                                <v-card-text>
                                                    <v-container fluid>
                                                        <v-row>
                                                            <v-col class="mt-n9" cols="6" v-for="header in headers"
                                                                :key="header.value">
                                                                <v-checkbox v-if="header.text" v-model="selectedHeaders"
                                                                    :label="header.text" color="primary"
                                                                    :value="header.text" hide-details></v-checkbox>
                                                            </v-col>
                                                        </v-row>
                                                    </v-container>
                                                </v-card-text>
                                                <v-layout justify-center>
                                                    <v-card-actions class="mb-5">
                                                        <v-spacer></v-spacer>
                                                        <v-btn outlined color="primary" @click="resetDefault"
                                                            width="190">
                                                            {{ $t("farm.restoreDefault") }}
                                                        </v-btn>
                                                        <v-btn color="primary" @click="resetTableStructure" width="190">
                                                            {{ $t("farm.apply") }}
                                                        </v-btn>
                                                    </v-card-actions>
                                                </v-layout>
                                            </v-card>
                                        </v-menu>


                                        <v-tooltip top color="black" max-width="350">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn class="mx-2" fab small outlined color="primary" v-bind="attrs"
                                                    v-on="on" @click="printPDF">
                                                    <v-icon dark>mdi-printer</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>{{ $t("farm.print") }}</span>
                                        </v-tooltip>
                                        </div>
                                    </div>
                                </template>

                                <!-- Individual column templates -->
                                <template v-slot:item.user="{ item }">
                                    <div class="font-weight-medium">{{ item.userName }}</div>
                                </template>

                                <template v-slot:item.userRole="{ item }">
                                    <span>{{ item.userRole }}</span>
                                </template>

                                <template v-slot:item.ipAddress="{ item }">
                                    <span>{{ item.ipAddress || '-' }}</span>
                                </template>

                                <template v-slot:item.dateTime="{ item }">
                                    <span>{{ item.dateTime }}</span>
                                </template>

                                <template v-slot:item.module="{ item }">
                                    <span>{{ item.module }}</span>
                                </template>

                                <template v-slot:item.activityDescription="{ item }">
                                    <span>{{ item.activityDescription }}</span>
                                </template>

                                <template v-slot:footer></template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </v-container>
    </div>
</template>

<script>
import loadingMixin from "@/mixins/LoadingMixin";
import download from "@/mixins/DownloadMixin";
import GeneralMixin from "@/mixins/GeneralMixin";
import html2pdf from "html2pdf.js";
import printJS from "print-js";
import FarmService from "@/_services/FarmService";

export default {
    name: "ActivityLog",
    props: {
        farm: {
            type: Object,
            default: () => ({}),
        },
    },
    async mounted() {
        // Initialize user role list with translations
        this.userRoleList = [
            { name: this.$t("farm.all"), value: "" },
            { name: this.$t("farm.farmer"), value: "farmer" },
            { name: this.$t("farm.technician"), value: "technician" },
        ];
        
        // Initialize headers with translations
        this.headers = [
            {
                text: this.$t("farm.user"),
                align: "start",
                value: "user",
                class: "black--text",
                sortable: true,
            },
            {
                text: this.$t("farm.userRole"),
                align: "start",
                value: "userRole",
                class: "black--text",
                sortable: true,
            },
            {
                text: this.$t("farm.ipAddress"),
                align: "start",
                value: "ipAddress",
                class: "black--text",
                sortable: true,
            },
            {
                text: this.$t("farm.dateAndTime"),
                align: "start",
                value: "dateTime",
                class: "black--text",
                sortable: true,
            },
            {
                text: this.$t("farm.module"),
                align: "start",
                value: "module",
                class: "black--text",
                sortable: true,
            },
            {
                text: this.$t("farm.activityDescription"),
                align: "start",
                value: "activityDescription",
                class: "black--text",
                sortable: true,
            },
        ];
        
        // Initialize allSelectedHeaders with translations
        this.allSelectedHeaders = [
            this.$t("farm.user"),
            this.$t("farm.userRole"),
            this.$t("farm.ipAddress"),
            this.$t("farm.dateAndTime"),
            this.$t("farm.module"),
            this.$t("farm.activityDescription"),
        ];
        
        this.tableHeaders = this.headers;
        this.selectedHeaders = this.allSelectedHeaders;
        this.getActivity();
    },
    computed: {
        totalPages() {
            return parseInt(Math.ceil(this.totalActivities / this.options.limit));
        },
        currentPageStart() {
            return this.options.page;
        },
        filteredActivities() {
            return this.activities;
        },
    },
    watch: {
        options: {
            handler() {
                if (!this.isUpdatingOptions) {
                    this.getActivity();
                }
            },
            deep: true,
        },
    },
    data() {
        return {
            debounceTimer: null,
            isUpdatingOptions: false,
            search: "",
            userRole: "",
            userRoleList: [],
            loading: false,
            options: {
                limit: 10,
                page: 1,
            },
            headers: [],
            allSelectedHeaders: [],
            menu: false,
            activities: [],
            totalActivities: 0,
            itemsPerPageOptions: [5, 10, 25, 50],
            itemsPerPageSlider: 1,
            selectedHeaders: [],
            tableHeaders: [],
        };
    },
    methods: {
        async getActivity() {
            this.startLoading();
            try {
                const params = {
                    farmId: this.farm.id,
                    page: this.options.page,
                    limit: this.options.limit,
                };
                
                // Only include search and userRole if they have values
                if (this.search && this.search.trim()) {
                    params.search = this.search.trim();
                }
                if (this.userRole && this.userRole.trim()) {
                    params.userRole = this.userRole.trim();
                }
                
                const response = await FarmService.getFarmActivityLog(params);
                if (response.success) {
                    // Map API response fields to component fields
                    this.activities = response.data.activities.map(activity => ({
                        ...activity,
                        dateTime: activity.createdAt ? this.formatDate(activity.createdAt, 'DD-MMM-YYYY HH:mm:ss') : activity.createdAt,
                        activityDescription: activity.activitydesc
                    }));
                    this.totalActivities = response.data.totalActivities;
                } else {
                    console.error("Failed to fetch activity log:", response.message);
                }
            } catch (error) {
                console.error("Error fetching activity log:", error);
            } finally {
                this.stopLoading();
            }
        },
        searchQuery() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.isUpdatingOptions = true;
                this.options.page = 1;
                this.$nextTick(() => {
                    this.isUpdatingOptions = false;
                    this.getActivity();
                });
            }, 500);
        },
        handleRoleChange() {
            this.isUpdatingOptions = true;
            this.options.page = 1;
            this.$nextTick(() => {
                this.isUpdatingOptions = false;
                this.getActivity();
            });
        },
        pageChange(next) {
            this.isUpdatingOptions = true;
            if (next) this.options.page += 1;
            else this.options.page -= 1;
            this.$nextTick(() => {
                this.isUpdatingOptions = false;
                this.getActivity();
            });
        },
        async resetDefault() {
            this.tableHeaders = this.headers;
            this.selectedHeaders = this.allSelectedHeaders;
            this.isUpdatingOptions = true;
            this.options.limit = this.itemsPerPageOptions[this.itemsPerPageSlider];
            this.options.page = 1;
            this.menu = false;
            this.$nextTick(() => {
                this.isUpdatingOptions = false;
                this.getActivity();
            });
        },
        async resetTableStructure() {
            this.tableHeaders = this.headers.filter((header) =>
                this.selectedHeaders.includes(header.text)
            );
            this.isUpdatingOptions = true;
            this.options.limit = this.itemsPerPageOptions[this.itemsPerPageSlider];
            this.options.page = 1;
            this.menu = false;
            this.$nextTick(() => {
                this.isUpdatingOptions = false;
                this.getActivity();
            });
        },
        async printPDF() {
            await this.$nextTick();
            const element = document.querySelector(".v-data-table__wrapper");
            let opt = {
                margin: 1,
                filename: "FarmActivityLog.pdf",
                image: { type: "jpeg", quality: 0.95 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
            };
            let worker = await html2pdf()
                .set(opt)
                .from(element)
                .toCanvas()
                .outputPdf("blob", "FarmActivityLog.pdf");
            let file = new File([worker], "FarmActivityLog.pdf", { type: "application/pdf" });
            var pdfUrl = await URL.createObjectURL(file);
            await printJS({ printable: `${pdfUrl}`, type: "pdf", showModal: true });
            await this.$nextTick();
        },
    },
    mixins: [loadingMixin, download, GeneralMixin],
};
</script>

<style lang="scss" scoped>
::v-deep .v-data-table-header span {
    text-transform: uppercase !important;
    color: black;
}

.control {
    min-width: 120px;
    max-width: 220px;
    flex: 1 1 160px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
}

/* Hide elements when printing */
@media print {
    .hide-on-print {
        display: none !important;
    }
}
</style>
